/**
 * Pure cursor-loop fetch for ALL uncategorized rows — extracted from
 * `useUncategorizedTransactions` so it's testable WITHOUT mocking `@/lib/api`
 * or the hook module. Sibling tests `mock.module("@/hooks/use-uncategorized-
 * transactions", …)` (process-global, leaks across files on CI), so importing a
 * testable export from the hook module itself resolves against that mock and
 * fails. Keeping the logic here — a module nobody mocks — sidesteps the leak.
 */

import type { TransactionRow } from "./use-transactions"

export interface UncategorizedResponse {
  success: boolean
  data: TransactionRow[]
  nextCursor?: string | null
  hasMore?: boolean
  total?: number
}

/**
 * Cursor-loop through ALL uncategorized rows (no hard 200-row cap). `get` is
 * dependency-injected so tests can drive it directly. `pageCap` is defensive
 * insurance against a server that never stops returning a cursor — at 200/page
 * it allows up to 50 000 rows.
 */
export async function fetchAllUncategorized(
  get: <T>(url: string) => Promise<T>,
  pageCap = 250,
): Promise<TransactionRow[]> {
  const allRows: TransactionRow[] = []
  let cursor: string | null = null
  let pages = 0
  do {
    const url: string = cursor
      ? `/transactions?uncategorized=true&limit=200&cursor=${encodeURIComponent(cursor)}`
      : "/transactions?uncategorized=true&limit=200"
    const page: UncategorizedResponse = await get<UncategorizedResponse>(url)
    allRows.push(...page.data)
    cursor = page.nextCursor ?? null
  } while (cursor && ++pages < pageCap)
  return allRows
}
